import React, { createContext, useContext, useCallback } from 'react';

const CacheInvalidationContext = createContext();

/**
 * Hook to use cache invalidation
 * Call invalidateCache('students') to refresh student cache
 * Call invalidateCache('families') to refresh family cache
 */
export const useCacheInvalidation = () => {
    const context = useContext(CacheInvalidationContext);
    if (!context) {
        throw new Error('useCacheInvalidation must be used within a CacheInvalidationProvider');
    }
    return context;
};

export const CacheInvalidationProvider = ({ children }) => {
    // Map to store refresh functions for each resource type
    const refreshFunctionsRef = React.useRef(new Map());

    /**
     * Register a refresh function for a resource type
     * Called by each context (StudentContext, TutorContext, etc.)
     */
    const registerRefresh = useCallback((resourceType, refreshFunction) => {
        refreshFunctionsRef.current.set(resourceType, refreshFunction);

        // Cleanup function
        return () => {
            refreshFunctionsRef.current.delete(resourceType);
        };
    }, []);

    /**
     * Invalidate cache for a specific resource type
     * Automatically calls the registered refresh function
     */
    const invalidateCache = useCallback((resourceType) => {
        const refreshFunction = refreshFunctionsRef.current.get(resourceType);
        if (refreshFunction) {
            refreshFunction();
        }
    }, []);

    /**
     * Invalidate multiple caches at once
     */
    const invalidateCaches = useCallback((resourceTypes) => {
        resourceTypes.forEach(resourceType => {
            invalidateCache(resourceType);
        });
    }, [invalidateCache]);

    const value = {
        registerRefresh,
        invalidateCache,
        invalidateCaches
    };

    return (
        <CacheInvalidationContext.Provider value={value}>
            {children}
        </CacheInvalidationContext.Provider>
    );
};

export default CacheInvalidationContext;
